/* eslint-disable @typescript-eslint/no-use-before-define */
import { TFunction } from 'i18next';
import { toast } from 'react-toastify';

import { Authenticated } from '../app/AuthorizationProvider';
import { PtoRequest, ShiftSwapRequest } from '../graphql/components';

export const getErrorResponseMessage = async (response: Response, t: TFunction) => {
  const responseBody = await response.json();
  const count = responseBody.relatedEntities?.length ?? 1;
  const messageKey = `response.${responseBody.errorCode}`;
  const messageText = t(messageKey, { count });
  // If error message text not found return generic text
  return messageText === messageKey ? t(`response.undefined`) : messageText;
};

export const customFetch =
  (t: TFunction, forceUpdateToken: Authenticated['forceUpdateToken']) =>
  async (request: RequestInfo, init: RequestInit) => {
    const response = await fetch(request, init);
    const responseToReturn = response.clone(); // To be able pass down full error code

    if (response.status >= 500) {
      // Server is down
      toast.error(t(`response.unavailable`));
    } else if (response.status >= 400) {
      if (isUnauthorized(response.status)) {
        forceUpdateToken();
      }

      // Bad requests
      if ((init.method === 'GET' && [400, 403, 404, 409, 422].includes(response.status)) || init.method !== 'GET') {
        const message = await getErrorResponseMessage(response, t);
        toast.warning(message);
      } else {
        toast.warning(t(`response.undefined`));
      }
    } else if (response.status >= 300) {
      // Service is down
      toast.warning(t(`response.undefined`));
    } else if (response.status === 200 && init.method === 'POST') {
      // replacing GET request having body sent => no response message should be shown except RTA Notifications
      if (String(request).includes('delayed-notifications/')) {
        toast.success(await getSuccessMessage(String(request), String(init.method), t, init, response));
      }
    } else if (response.status >= 200 && init.method !== 'GET') {
      // Do not show success message on subscribe to push notifications
      if (String(request).match(/push-notifications\/domains\/.+\/subscribe$/)) {
        return responseToReturn;
      }

      // Success response
      toast.success(await getSuccessMessage(String(request), String(init.method), t, init, response));

      if (response.status === 207) {
        // Along with success message, an additional warning message should be displayed as there was some side-effect error detected
        const message = await getErrorResponseMessage(response, t);
        toast.warning(message);
      }
    }

    return responseToReturn;
  };

export const isUnauthorized = (httpStatusCode: number) => {
  const unAuthorized = [401, 403].includes(httpStatusCode);
  if (unAuthorized) {
    console.warn('Request not authorized.');
  }

  return unAuthorized;
};

const getSuccessMessage = async (uri: string, method: string, t: TFunction, init: RequestInit, response: Response) => {
  let prefix: string;
  let count = 1;

  // historical-data-upload has to be first because it's within forecasting service
  if (uri.includes('/historical-data-upload')) {
    prefix = 'dataImport';
  } else if (uri.includes('forecasting/forecasts/production')) {
    prefix = 'forecasting.production';
  } else if (uri.includes('forecasting/intraday/v1/notification/settings')) {
    prefix = 'intradayPushNotificationSettings';
  } else if (uri.includes('forecasting/intraday/v1/notification')) {
    prefix = 'intradayNotifications';
  } else if (uri.match('notification\\/v1\\/users\\/.*\\/notification-settings')) {
    const requestBody = getRequestBody(init);

    prefix = `notification.settings.${requestBody.enabled ? 'enabled' : 'disabled'}`;
  } else if (uri.includes('forecasting/')) {
    prefix = 'forecasting';
  } else if (uri.includes('scheduling/schedules/production')) {
    prefix = 'scheduling.production';
  } else if (uri.includes('scheduling/pto')) {
    const requestBody = getRequestBody(init) as PtoRequest;
    let requestDetails: string = requestBody.type;

    if (method === 'PUT') {
      const responseBody = (await response.json()) as PtoRequest;
      requestDetails = `${responseBody.type}.${responseBody.state}`;
    }

    prefix = `scheduling.PTO.${requestDetails}`;
  } else if (uri.includes('scheduling/shift-swap')) {
    prefix = `scheduling.shiftSwap`;

    if (method === 'PUT') {
      const responseBody = (await response.json()) as ShiftSwapRequest;
      prefix = `scheduling.shiftSwap.${responseBody.state}`;
    }
  } else if (uri.includes('scheduling/schedules/adherence/settings')) {
    prefix = 'adherenceSettings';
  } else if (uri.includes('scheduling/schedule-elements')) {
    prefix = 'scheduling.scheduleElements';
  } else if (uri.includes('scheduling/')) {
    prefix = 'scheduling';
  } else if (uri.includes('administration/agents') || uri.match('\\/administration\\/supervisors\\/.*\\/agents\\/')) {
    // update of supervised agents has uri: /_rest/administration/supervisors/{supervisorId}/agents/{agentId}
    if (uri.includes('bulk-assignment')) {
      // This may affect multiple people
      const requestBody = getRequestBody(init);
      count = requestBody.agentsIds.length;
    }
    prefix = 'people';
  } else if (uri.includes('queue-management/skills')) {
    prefix = 'skills';
  } else if (uri.includes('queue-management/queues')) {
    prefix = 'queues';
  } else if (uri.includes('auto-scheduler/shift-templates')) {
    prefix = 'shiftTemplate';
  } else if (uri.includes('auto-scheduler/')) {
    prefix = 'autoScheduler';
  } else if (uri.includes('delayed-notifications/')) {
    prefix = 'rtaNotifications';
  } else {
    prefix = 'default';
  }

  return t(`response.success.${prefix}.${method}`, { count });
};

const getRequestBody = (init: RequestInit) => (typeof init.body === 'string' ? JSON.parse(init.body) : {});
